import { FluidProgressType, FluidWeight } from '@lmig/fluid-core';
import { FunctionalComponent, h } from '@stencil/core';

import { FluidFunctionalComponentProps } from '../../../model/functional/fluid-functional-component-props.interface';

interface LinearProgressProps extends FluidFunctionalComponentProps {
  determinate?: boolean;
  type?: FluidProgressType;
  value?: number;
  max?: number;
  progressValue?: number;
  weight?: FluidWeight;
  label?: string;
  labelId?: string;
}

// ================================================================ //
// -- Public Functional Components

/**
 * LinearProgressLabel - Label displayed above progress bar
 */
export const LinearProgressLabel: FunctionalComponent<LinearProgressProps> = (
  props: LinearProgressProps
) => {
  const { label, labelId } = props;

  return (
    !!label && (
      <div class='label-container'>
        <span id={labelId} class='label-text'>
          {label}
        </span>
      </div>
    )
  );
};

/**
 * LinearProgressIndicator - Progress bar
 */
export const LinearProgressIndicator: FunctionalComponent<
  LinearProgressProps
> = (props: LinearProgressProps) => {
  const { determinate, value, max, progressValue, weight, label, labelId } =
    props;
  const offsetX = (100 - progressValue) * -1;
  const determinateAriaAttrs = {
    'aria-valuemin': 0,
    'aria-valuemax': max,
    'aria-valuenow': value,
  };

  return (
    <div class='indicator-container'>
      <div
        role='progressbar'
        class={`indicator-body weight-${weight}`}
        aria-labelledby={!!label && labelId}
        aria-label={!label && 'Progress'}
        {...(!!determinate && determinateAriaAttrs)}>
        <span
          style={
            !!determinate && {
              transform: `translateX(${offsetX}%)`,
            }
          }
          class={{
            'indicator-progress-bar': true,
            determinate: determinate,
            indeterminate: !determinate,
          }}
        />
      </div>
      <IndicatorValue {...props} />
    </div>
  );
};

// ================================================================ //
// -- Private Functional Components

/**
 * IndicatorValue - Numeric representation of progress bar state
 */
const IndicatorValue: FunctionalComponent<LinearProgressProps> = (
  props: LinearProgressProps
) => {
  const { determinate, type, value, max, progressValue } = props;

  return (
    !!determinate && (
      <div class='indicator-value'>
        <span class='indicator-value-text'>
          {type === FluidProgressType.PERCENT
            ? `${progressValue}%`
            : `${Math.trunc(value)} / ${max}`}
        </span>
      </div>
    )
  );
};
